import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Search, Pencil, Trash2, Check, Ban, UserCheck, LogIn,
} from 'lucide-react';
import { api } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

interface User {
  id: string;
  full_name: string;
  email: string;
  app_role: string;
  approval_status: string;
  email_verified: boolean;
  last_login_at: string | null;
  created_at: string;
  avatar_url: string | null;
  deleted_at: string | null;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export function UsersPage() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState(0);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editName, setEditName] = useState('');
  const [editDays, setEditDays] = useState<string[]>([]);
  const [editHours, setEditHours] = useState(8);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const params: Record<string, string> = { limit: '20' };
  if (debouncedSearch) params.search = debouncedSearch;
  if (statusFilter !== 'all') params.approval_status = statusFilter;
  if (roleFilter !== 'all') params.app_role = roleFilter;
  if (cursors[page]) params.cursor = cursors[page]!;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', { debouncedSearch, statusFilter, roleFilter, page }],
    queryFn: () => api.get('/admin/users', { params }).then(r => r.data),
  });

  const users: User[] = data?.data ?? [];
  const pagination = data?.pagination;
  const totalCount = pagination?.total_count ?? 0;

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    queryClient.invalidateQueries({ queryKey: ['admin-pending-count'] });
  };

  const approveMut = useMutation({
    mutationFn: (u: User) => api.post(`/admin/users/${u.id}/approve`),
    onSuccess: () => { invalidateAll(); toast({ title: 'User approved successfully' }); },
    onError: () => toast({ title: 'Failed to approve user', variant: 'destructive' }),
  });
  const blockMut = useMutation({
    mutationFn: (id: string) => api.post(`/admin/users/${id}/block`),
    onSuccess: () => { invalidateAll(); toast({ title: 'User blocked successfully' }); },
    onError: () => toast({ title: 'Failed to block user', variant: 'destructive' }),
  });
  const activateMut = useMutation({
    mutationFn: (id: string) => api.post(`/admin/users/${id}/unblock`),
    onSuccess: () => { invalidateAll(); toast({ title: 'User activated successfully' }); },
    onError: () => toast({ title: 'Failed to activate user', variant: 'destructive' }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/users/${id}`),
    onSuccess: () => { invalidateAll(); toast({ title: 'User deleted successfully' }); setDeleteOpen(false); },
    onError: () => toast({ title: 'Failed to delete user', variant: 'destructive' }),
  });
  const [impersonatingUser, setImpersonatingUser] = useState<User | null>(null);
  const impersonateMut = useMutation({
    mutationFn: (u: User) => { setImpersonatingUser(u); return api.post('/admin/impersonate', { target_user_id: u.id }); },
    onSuccess: (res) => {
      const { token, session_id } = res.data.data;
      const u = impersonatingUser;
      sessionStorage.setItem('impersonation_admin_token', localStorage.getItem('access_token') || '');
      sessionStorage.setItem('impersonation_session_id', session_id);
      localStorage.setItem('impersonation_admin', JSON.stringify({
        adminId: currentUser?.id,
        targetUserId: u?.id,
        targetUserName: u?.full_name || u?.email,
        targetUserEmail: u?.email,
        startedAt: new Date().toISOString(),
      }));
      localStorage.setItem('access_token', token);
      toast({ title: 'Impersonation Active', description: 'Use the banner to return.' });
      window.location.href = '/dashboard';
    },
    onError: (err: any) => toast({ title: 'Impersonation Failed', description: err?.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  function statusBadge(user: User) {
    if (user.deleted_at) return <Badge variant="outline" className="bg-muted text-muted-foreground">Deleted</Badge>;
    switch (user.approval_status) {
      case 'pending': return <Badge className="bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-0">Pending</Badge>;
      case 'approved': return <Badge className="bg-green-500/20 text-green-600 dark:text-green-400 border-0">Approved</Badge>;
      case 'blocked': return <Badge variant="destructive">Blocked</Badge>;
      default: return <Badge variant="outline">{user.approval_status}</Badge>;
    }
  }

  function openEdit(user: User) {
    setEditUser(user);
    setEditName(user.full_name || '');
    setEditDays(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
    setEditHours(8);
    setEditOpen(true);
  }

  function resetPage() { setPage(0); setCursors([undefined]); }

  const isCurrentUser = (id: string) => id === currentUser?.id;

  return (
    <div className="space-y-6">
      <PageHeader title="User Management" description="View and manage all system users" />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name or email..." value={search} onChange={e => { setSearch(e.target.value); resetPage(); }} className="pl-10" />
        </div>
        <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); resetPage(); }}>
          <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="All Roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="member">Member</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); resetPage(); }}>
          <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-4"><Skeleton className="h-12 w-full" /><Skeleton className="h-[400px] w-full" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {debouncedSearch || roleFilter !== 'all' || statusFilter !== 'all'
            ? 'No users found matching your filters'
            : 'No users in the system'}
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id} className={user.deleted_at ? 'opacity-60' : ''}>
                    <TableCell className="font-medium">
                      <Link to={`/admin/users/${user.id}`} className="hover:underline text-primary">
                        {user.full_name || 'Unnamed'}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{user.app_role}</Badge>
                    </TableCell>
                    <TableCell>{statusBadge(user)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Approve (pending only) */}
                        {user.approval_status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={() => approveMut.mutate(user)}
                            disabled={approveMut.isPending}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950">
                            <Check className="h-4 w-4 mr-1" /> Approve
                          </Button>
                        )}
                        {/* Block (pending or approved) */}
                        {(user.approval_status === 'pending' || user.approval_status === 'approved') && !isCurrentUser(user.id) && (
                          <Button size="sm" variant="outline" onClick={() => blockMut.mutate(user.id)}
                            disabled={blockMut.isPending}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950">
                            <Ban className="h-4 w-4 mr-1" /> Block
                          </Button>
                        )}
                        {/* Activate (blocked only) */}
                        {user.approval_status === 'blocked' && (
                          <Button size="sm" variant="outline" onClick={() => activateMut.mutate(user.id)}
                            disabled={activateMut.isPending}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950">
                            <UserCheck className="h-4 w-4 mr-1" /> Activate
                          </Button>
                        )}
                        {/* Login As (approved, not self, not admin) */}
                        {user.approval_status === 'approved' && !isCurrentUser(user.id) && user.app_role !== 'admin' && (
                          <Button size="sm" variant="outline" onClick={() => impersonateMut.mutate(user)}
                            disabled={impersonateMut.isPending}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950">
                            <LogIn className="h-4 w-4 mr-1" /> Login as
                          </Button>
                        )}
                        {/* Edit */}
                        <Button size="icon" variant="ghost" onClick={() => openEdit(user)} title="Edit user">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {/* Delete */}
                        <Button size="icon" variant="ghost" onClick={() => { setDeleteUser(user); setDeleteOpen(true); }}
                          disabled={isCurrentUser(user.id)} title={isCurrentUser(user.id) ? 'Cannot delete yourself' : 'Delete user'}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {Math.min(users.length, 20)} of {totalCount} users
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Previous</Button>
              <Button variant="outline" size="sm" disabled={!pagination?.has_more} onClick={() => {
                if (pagination?.cursor) { setCursors(prev => { const n = [...prev]; n[page + 1] = pagination.cursor!; return n; }); setPage(p => p + 1); }
              }}>Next</Button>
            </div>
          </div>
        </>
      )}

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
            <DialogDescription>Update user profile information</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={editName} onChange={e => setEditName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Working Days</Label>
                <div className="grid grid-cols-2 gap-2">
                  {DAYS.map(day => (
                    <label key={day} className="flex items-center space-x-2 cursor-pointer">
                      <Checkbox
                        checked={editDays.includes(day)}
                        onCheckedChange={checked => {
                          setEditDays(prev => checked ? [...prev, day] : prev.filter(d => d !== day));
                        }}
                      />
                      <span className="text-sm capitalize">{day}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Daily Hours</Label>
                <Input type="number" step="0.5" value={editHours} onChange={e => setEditHours(parseFloat(e.target.value))} />
              </div>
              <div className="text-sm text-muted-foreground">
                Weekly Capacity: {(editHours * editDays.length).toFixed(1)} hours/week ({editHours}h × {editDays.length} days)
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                <Button onClick={() => { toast({ title: 'Profile updated' }); setEditOpen(false); }}>
                  Save changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Are you sure?"
        description={`This will soft delete ${deleteUser?.full_name || deleteUser?.email}. The user will be marked as deleted and banned from the system. You can restore them later if needed.`}
        confirmLabel="Delete User"
        variant="destructive"
        onConfirm={() => { if (deleteUser) deleteMut.mutate(deleteUser.id); }}
        isLoading={deleteMut.isPending}
      />
    </div>
  );
}
